import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getHome, listCategories, listProducts } from 'api/catalog';
import Button from 'components/Button';
import Container from 'components/Container';
import EmptyState from 'components/EmptyState';
import { BLOCKS, HeroSkeleton } from 'components/home';
import useAsync from 'hooks/useAsync';
import { useStore } from 'context/StoreContext';
import usePageReady from 'utils/usePageReady';
import usePageTitle from 'utils/usePageTitle';
import { buildDefaultHome, DEFAULT_NEW_PRODUCTS } from './homeDefault';

/**
 * The shop's home blocks (shop content), or, when it has none yet, its
 * categories and newest products. The default blocks' headings are UI text,
 * so they are built at render time (see Home) and follow the language.
 */
async function loadHome() {
  const { value } = await getHome();
  const blocks = (value && value.blocks) || [];
  if (blocks.length) return { value: { blocks, isDefault: false } };

  const [categories, products] = await Promise.all([
    listCategories(),
    listProducts({ sort: 'newest', limit: DEFAULT_NEW_PRODUCTS }),
  ]);
  return { value: { isDefault: true, categories: categories.value || [], products: products.value || [] } };
}

export default function Home() {
  const { t } = useTranslation();
  const { identity } = useStore();
  usePageTitle(null, { description: identity.tagline });
  const { status, value: loaded, reload } = useAsync(() => loadHome(), []);
  usePageReady(status !== 'loading');
  const value = useMemo(
    () => (loaded && loaded.isDefault ? { ...buildDefaultHome({ ...loaded, t }), isDefault: true } : loaded),
    [loaded, t]
  );

  if (status === 'loading') return <HeroSkeleton />;
  if (status === 'error') {
    return (
      <Container>
        <EmptyState as="h1" title={t('home.errorTitle')} description={t('home.errorBody')}>
          <Button onClick={reload}>{t('status.retry')}</Button>
        </EmptyState>
      </Container>
    );
  }

  if (value.isDefault && value.empty) {
    return (
      <Container>
        <EmptyState as="h1" title={t('home.preparingTitle', { name: identity.name })} description={t('home.preparingBody')} />
      </Container>
    );
  }

  return (
    <>
      {value.isDefault && (
        <Container>
          <h1 className="pt-10 font-display text-3xl text-ink sm:text-4xl lg:pt-14">{identity.name}</h1>
          {identity.tagline && <p className="mt-3 max-w-2xl text-pretty text-muted">{identity.tagline}</p>}
        </Container>
      )}
      {value.blocks.map((block) => {
        const Block = BLOCKS[block.type];
        return Block ? <Block key={block.id || block.type} block={block} /> : null;
      })}
    </>
  );
}
